/**
 * Warranty Policy Detail Page.
 *
 * A "policy" is a server-side grouping of `AvailabilityRule` rows that share
 * the same `policy_code`. This page shows everything linked to one policy:
 * the warranty templates it routes to, the products it's mapped to, and each
 * underlying rule. Editing affordances reuse the shared PolicyMappingDialog.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { ArrowLeft, Copy, Pencil, Plus, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { useFormatting } from '@/admin/hooks/use-formatting'
import {
  type AvailabilityRule,
  type PolicyGroupProductSummary,
  type PolicyGroupSummary,
  useBulkDeleteAvailabilityRules,
  useCreateAvailabilityRule,
  usePolicyGroupsPage,
} from '@/admin/hooks/use-availability-rules'
import {
  flattenProductCategories,
  useProductCategories,
  useProductTemplates,
} from '@/admin/hooks/use-products'
import { useWarrantyTemplates } from '@/admin/hooks/use-warranties'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { toastApiError } from '@/shared/lib/api-error'
import PolicyMappingDialog from './policy-mapping-dialog'
import {
  makePolicyRuleCloneInput,
  readRouting,
  suggestNextYearPolicyCode,
} from './policy-utils'
import { warrantyTypeLabel } from '@/admin/pages/transfers/components/warranty-label'

function SectionHeader({
  title,
  count,
  trailing,
}: {
  title: string
  count?: number
  trailing?: React.ReactNode
}) {
  return (
    <div className="mb-3 flex items-center justify-between border-b-[0.5px] border-[var(--rule)] pb-2">
      <span className="text-[10px] font-normal uppercase tracking-[0.12em] text-[var(--ink-3)]">
        {title}
        {typeof count === 'number' ? ` (${count})` : ''}
      </span>
      {trailing}
    </div>
  )
}

function severityBadgeVariant(
  severity: AvailabilityRule['severity'],
): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (severity) {
    case 'BLOCK':
      return 'destructive'
    case 'WARN':
      return 'secondary'
    default:
      return 'outline'
  }
}

export function WarrantyPolicyDetailPage() {
  const { policyCode = '' } = useParams<{ policyCode: string }>()
  const navigate = useNavigate()
  const { formatDate } = useFormatting()
  const formatPolicyDate = (value: string | null): string =>
    value ? formatDate(value) : '-'

  // Use the existing list endpoint with a search filter, then exact-match by
  // policy_code. The backend search is fuzzy/contains so we narrow client-side.
  const { data: page, isLoading } = usePolicyGroupsPage({
    search: policyCode,
    page_size: 50,
  })
  const group = useMemo<PolicyGroupSummary | null>(
    () => page?.results?.find((g) => g.policy_code === policyCode) ?? null,
    [page, policyCode],
  )

  const { data: warrantyTemplates = [] } = useWarrantyTemplates()
  const { data: productTemplates = [] } = useProductTemplates()
  const { data: productCategoriesTree = [] } = useProductCategories()
  const productCategories = useMemo(
    () => flattenProductCategories(productCategoriesTree),
    [productCategoriesTree],
  )

  const createRule = useCreateAvailabilityRule()
  const bulkDeleteRules = useBulkDeleteAvailabilityRules()

  const availableAttributes = useMemo(() => {
    const seen = new Set<string>()
    return productTemplates.flatMap((template) =>
      (template.attributes ?? []).filter((attribute) => {
        if (seen.has(attribute.code_name)) return false
        seen.add(attribute.code_name)
        return true
      }),
    )
  }, [productTemplates])

  const existingPolicyCodes = useMemo(
    () => page?.results?.map((g) => g.policy_code) ?? [],
    [page],
  )

  // Lookup option names by uuid (group payload has templates, not options).
  const optionNameByUuid = useMemo(() => {
    const map = new Map<string, string>()
    for (const tmpl of warrantyTemplates) {
      for (const opt of tmpl.options ?? []) {
        map.set(opt.uuid, opt.display_name)
      }
    }
    return map
  }, [warrantyTemplates])

  // Derive coverage routings used across rules (deduped by template+option).
  const coverageRoutings = useMemo(() => {
    if (!group) return []
    const seen = new Map<
      string,
      {
        templateUuid: string
        templateName: string
        warrantyType: string
        optionUuid: string
        optionName: string
        priorities: Set<number>
        ruleCount: number
      }
    >()
    for (const rule of group.rules) {
      const routing = readRouting(rule.effect_overrides)
      const key = `${routing.warranty_template_uuid}|${routing.warranty_template_option_uuid}`
      if (!routing.warranty_template_uuid) continue
      const tmpl = group.warranty_templates.find(
        (t) => t.uuid === routing.warranty_template_uuid,
      )
      const existing = seen.get(key)
      if (existing) {
        existing.priorities.add(routing.priority)
        existing.ruleCount += 1
      } else {
        seen.set(key, {
          templateUuid: routing.warranty_template_uuid,
          templateName: tmpl?.name ?? 'Unknown template',
          warrantyType: tmpl?.warranty_type ?? '',
          optionUuid: routing.warranty_template_option_uuid,
          optionName:
            optionNameByUuid.get(routing.warranty_template_option_uuid) ??
            (routing.warranty_template_option_uuid ? 'Unknown option' : '-'),
          priorities: new Set([routing.priority]),
          ruleCount: 1,
        })
      }
    }
    return Array.from(seen.values())
  }, [group, optionNameByUuid])

  // Dialog state ---------------------------------------------------------
  const [dialogState, setDialogState] = useState<{
    open: boolean
    mode: 'add-mapping' | 'edit-mapping'
    editing: AvailabilityRule | null
    sourceRule: AvailabilityRule | null
  }>({
    open: false,
    mode: 'add-mapping',
    editing: null,
    sourceRule: null,
  })

  const [removalState, setRemovalState] = useState<{
    product: PolicyGroupProductSummary
    rules: AvailabilityRule[]
  } | null>(null)

  const [groupDeletionOpen, setGroupDeletionOpen] = useState(false)

  const [duplicateState, setDuplicateState] = useState<{
    open: boolean
    action: 'clone' | 'next-year'
    nextPolicyCode: string
  }>({
    open: false,
    action: 'clone',
    nextPolicyCode: '',
  })

  function openAddMapping() {
    if (!group) return
    const sourceRule =
      group.rules.find((r) => r.scope === 'warranty_filter') ??
      group.rules[0] ??
      null
    if (!sourceRule) {
      toast.error('No source rule found for this policy.')
      return
    }
    setDialogState({
      open: true,
      mode: 'add-mapping',
      editing: null,
      sourceRule,
    })
  }

  function openEditRule(rule: AvailabilityRule) {
    setDialogState({
      open: true,
      mode: 'edit-mapping',
      editing: rule,
      sourceRule: rule,
    })
  }

  function openDuplicateDialog(action: 'clone' | 'next-year') {
    if (!group) return
    setDuplicateState({
      open: true,
      action,
      nextPolicyCode:
        action === 'next-year' ? suggestNextYearPolicyCode(group.policy_code) : '',
    })
  }

  function startProductRemoval(product: PolicyGroupProductSummary) {
    if (!group) return
    const rulesForProduct = group.rules.filter((r) => r.scope_id === product.uuid)
    if (rulesForProduct.length === 0) {
      toast.error('No mapping found for this product.')
      return
    }
    setRemovalState({ product, rules: rulesForProduct })
  }

  async function confirmRemoveProduct() {
    if (!removalState) return
    try {
      await bulkDeleteRules.mutateAsync(removalState.rules.map((r) => r.uuid))
      toast.success(
        removalState.rules.length === 1
          ? 'Product mapping removed'
          : `${removalState.rules.length} product mappings removed`,
      )
      setRemovalState(null)
    } catch (err) {
      toastApiError(err, 'Failed to remove product mapping')
    }
  }

  async function confirmDeleteGroup() {
    if (!group) return
    try {
      await bulkDeleteRules.mutateAsync(group.rules.map((r) => r.uuid))
      toast.success(`Policy ${group.policy_code} deleted`)
      setGroupDeletionOpen(false)
      navigate('/manage/warranties/policies')
    } catch (err) {
      toastApiError(err, 'Failed to delete policy')
    }
  }

  async function confirmDuplicatePolicy() {
    if (!group) return
    const next = duplicateState.nextPolicyCode.trim().toUpperCase()
    if (!next) {
      toast.error('Policy number is required')
      return
    }
    if (
      existingPolicyCodes.some((c) => c.trim().toLowerCase() === next.toLowerCase())
    ) {
      toast.error('Policy number must be unique')
      return
    }
    try {
      for (const rule of group.rules) {
        await createRule.mutateAsync(makePolicyRuleCloneInput(rule, next))
      }
      toast.success(
        duplicateState.action === 'next-year'
          ? `Policy ${next} created for next year`
          : `Policy ${next} cloned`,
      )
      setDuplicateState({ open: false, action: 'clone', nextPolicyCode: '' })
      navigate(`/manage/warranties/policies/${encodeURIComponent(next)}`)
    } catch (err) {
      toastApiError(err, 'Failed to duplicate policy')
    }
  }

  // Render ---------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="text-[13px] text-[var(--ink-3)]">Loading…</p>
        </main>
      </AppShell>
    )
  }

  if (!group) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <Button
            variant="ghost"
            onClick={() => navigate('/manage/warranties/policies')}
            className="mb-6 flex h-auto cursor-pointer items-center gap-1 px-0 text-[11px] text-[var(--ink-3)]"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to policies
          </Button>
          <p className="text-[13px] text-[var(--ink-3)]">
            Policy {policyCode} not found.
          </p>
        </main>
      </AppShell>
    )
  }

  const warrantyTypes = Array.from(
    new Set(group.warranty_types.map(warrantyTypeLabel)),
  )

  return (
    <AppShell>
      <main className="page-container py-10">
        {/* Back */}
        <Button
          variant="ghost"
          onClick={() => navigate('/manage/warranties/policies')}
          className="mb-6 flex h-auto cursor-pointer items-center gap-1 px-0 text-[11px] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to policies
        </Button>

        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <h1 className="clara-title m-0 font-mono text-[var(--ink)]">
                {group.policy_code}
              </h1>
            </div>
            <p className="text-[13px] text-[var(--ink-3)]">
              {warrantyTypes.length > 0 ? warrantyTypes.join(', ') : 'No warranty type'}
              <> &middot; {group.rule_count} rule{group.rule_count === 1 ? '' : 's'}</>
              <> &middot; {group.mapped_products.length} mapped product{group.mapped_products.length === 1 ? '' : 's'}</>
              <> &middot; {formatPolicyDate(group.start_date)} → {formatPolicyDate(group.end_date)}</>
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={openAddMapping}
              className="flex cursor-pointer items-center gap-1.5 text-[11px] uppercase tracking-[0.1em]"
            >
              <Plus className="h-3 w-3" />
              Add product
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openDuplicateDialog('clone')}
              className="flex cursor-pointer items-center gap-1.5 text-[11px] uppercase tracking-[0.1em]"
            >
              <Copy className="h-3 w-3" />
              Clone
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openDuplicateDialog('next-year')}
              className="flex cursor-pointer items-center gap-1.5 text-[11px] uppercase tracking-[0.1em]"
            >
              New year
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGroupDeletionOpen(true)}
              className="flex cursor-pointer items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--status-error)] hover:text-[var(--status-error)]"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </Button>
          </div>
        </div>

        {/* Coverage */}
        <div className="mb-8">
          <SectionHeader title="Coverage" count={coverageRoutings.length} />
          {coverageRoutings.length === 0 ? (
            <p className="text-[12px] text-[var(--ink-3)]">
              No coverage routing configured.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {coverageRoutings.map((cov) => (
                <div
                  key={`${cov.templateUuid}|${cov.optionUuid}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-3"
                >
                  <div className="flex min-w-0 flex-col gap-1">
                    <Link
                      to={`/manage/warranties/templates/${cov.templateUuid}/overview`}
                      className="text-[13px] text-[var(--green)] hover:underline"
                    >
                      {cov.templateName}
                    </Link>
                    <span className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--ink-3)]">
                      <span>Option: {cov.optionName}</span>
                      {cov.warrantyType ? (
                        <Badge variant="outline" className="text-[10px]">
                          {warrantyTypeLabel(cov.warrantyType)}
                        </Badge>
                      ) : null}
                      <span>
                        Priority {Array.from(cov.priorities).sort((a, b) => a - b).join(', ')}
                      </span>
                      <span>
                        {cov.ruleCount} rule{cov.ruleCount === 1 ? '' : 's'}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mapped products */}
        <div className="mb-8">
          <SectionHeader
            title="Mapped products"
            count={group.mapped_products.length}
            trailing={
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={openAddMapping}
                className="cursor-pointer gap-1 text-[11px] text-[var(--ink-2)] hover:text-[var(--ink)]"
              >
                <Plus className="h-3 w-3" />
                Add product
              </Button>
            }
          />
          {group.mapped_products.length === 0 ? (
            <p className="text-[12px] text-[var(--ink-3)]">
              No products mapped to this policy yet.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {group.mapped_products.map((product) => (
                <li
                  key={product.uuid}
                  className="group inline-flex items-center gap-1 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-1 pl-2.5 pr-1 text-[12px] text-[var(--ink)] transition-colors duration-150 hover:border-[var(--rule-strong)]"
                >
                  <Link
                    to={`/manage/products/${product.uuid}`}
                    className="truncate hover:underline"
                  >
                    {product.cached_short_name}
                  </Link>
                  <button
                    type="button"
                    onClick={() => startProductRemoval(product)}
                    aria-label={`Remove ${product.cached_short_name}`}
                    className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-[3px] text-[var(--ink-3)] transition-colors duration-150 hover:bg-[var(--status-error)]/10 hover:text-[var(--status-error)]"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Rules */}
        <div className="mb-8">
          <SectionHeader title="Rules" count={group.rules.length} />
          <div className="flex flex-col gap-2">
            {group.rules.map((rule) => {
              const routing = readRouting(rule.effect_overrides)
              const product =
                rule.scope === 'product'
                  ? group.mapped_products.find((p) => p.uuid === rule.scope_id)
                  : null
              const scopeLabel =
                rule.scope === 'warranty_filter'
                  ? 'Filter'
                  : rule.scope === 'product'
                    ? `Product · ${product?.cached_short_name ?? rule.scope_id ?? '-'}`
                    : rule.scope
              const overrideEntries = Object.entries(routing.attribute_values)
              return (
                <div
                  key={rule.uuid}
                  className="flex flex-col gap-2 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-[13px] text-[var(--ink)]">
                        {rule.name || scopeLabel}
                      </span>
                      <span className="flex flex-wrap items-center gap-2 text-[11px] text-[var(--ink-3)]">
                        <Badge variant="outline" className="text-[10px]">
                          {scopeLabel}
                        </Badge>
                        <Badge
                          variant={severityBadgeVariant(rule.severity)}
                          className="text-[10px]"
                        >
                          {rule.severity}
                        </Badge>
                        {!rule.is_active && (
                          <Badge variant="secondary" className="text-[10px]">
                            Inactive
                          </Badge>
                        )}
                        <span>Priority {rule.priority}</span>
                        <span>
                          {formatPolicyDate(rule.effective_from)} → {formatPolicyDate(rule.effective_until)}
                        </span>
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => openEditRule(rule)}
                      className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
                      title="Edit rule"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                  {rule.message ? (
                    <p className="text-[12px] text-[var(--ink-2)]">
                      “{rule.message}”
                    </p>
                  ) : null}
                  {overrideEntries.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {overrideEntries.map(([code, value]) => (
                        <Badge
                          key={code}
                          variant="outline"
                          className="text-[10px]"
                        >
                          {code}: {String(value)}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>

        {/* Dialogs */}
        <PolicyMappingDialog
          attributes={availableAttributes}
          productTemplates={productTemplates}
          productCategories={productCategories}
          open={dialogState.open}
          onOpenChange={(open) => {
            setDialogState((prev) => ({ ...prev, open }))
            if (!open) {
              setDialogState({
                open: false,
                mode: 'add-mapping',
                editing: null,
                sourceRule: null,
              })
            }
          }}
          editing={dialogState.editing}
          sourceRule={dialogState.sourceRule}
          mode={dialogState.mode}
          policyCode={group.policy_code}
          existingPolicyCodes={existingPolicyCodes}
        />

        <ConfirmDeleteDialog
          open={!!removalState}
          onOpenChange={(open) => {
            if (!open) setRemovalState(null)
          }}
          title="Remove product mapping?"
          description={
            removalState ? (
              <>
                Remove <strong>{removalState.product.cached_short_name}</strong>{' '}
                from policy <strong>{group.policy_code}</strong>? This will delete{' '}
                {removalState.rules.length} rule
                {removalState.rules.length === 1 ? '' : 's'}.
              </>
            ) : null
          }
          onConfirm={confirmRemoveProduct}
          isPending={bulkDeleteRules.isPending}
        />

        <ConfirmDeleteDialog
          open={groupDeletionOpen}
          onOpenChange={setGroupDeletionOpen}
          title="Delete policy?"
          description={
            <>
              Delete policy <strong>{group.policy_code}</strong> along with all{' '}
              {group.rules.length} of its product mappings? This action cannot be
              undone.
            </>
          }
          onConfirm={confirmDeleteGroup}
          isPending={bulkDeleteRules.isPending}
        />

        <Dialog
          open={duplicateState.open}
          onOpenChange={(open) =>
            setDuplicateState((prev) => ({
              ...prev,
              open,
              nextPolicyCode: open ? prev.nextPolicyCode : '',
            }))
          }
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {duplicateState.action === 'next-year'
                  ? 'New policy for next year'
                  : 'Clone policy'}
              </DialogTitle>
              <DialogDescription>
                Duplicates {group.rules.length} mapping
                {group.rules.length === 1 ? '' : 's'} from policy{' '}
                {group.policy_code}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-2">
              <Label htmlFor="next-policy-code">New policy number</Label>
              <Input
                id="next-policy-code"
                value={duplicateState.nextPolicyCode}
                onChange={(event) =>
                  setDuplicateState((prev) => ({
                    ...prev,
                    nextPolicyCode: event.target.value.toUpperCase(),
                  }))
                }
                placeholder="Enter a unique policy number"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setDuplicateState({
                    open: false,
                    action: 'clone',
                    nextPolicyCode: '',
                  })
                }
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDuplicatePolicy}
                disabled={createRule.isPending}
              >
                {duplicateState.action === 'next-year'
                  ? 'Create next year'
                  : 'Clone'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </AppShell>
  )
}

export default WarrantyPolicyDetailPage
